// Pure reassembly of chunked, split deployment log segments into a structured,
// renderable shape. Kept out of the data layer and the UI so it can be unit
// tested in isolation (see logs.test.ts).
//
// The backend streams logs as a flat list of LogEntry segments, and a single
// step's log may be SPLIT across several entries that share the same
// task + step. This groups entries by task (first-seen order), then by step
// within each task (first-seen order), concatenating each step's `log` text in
// arrival order. A task's status reflects the latest segment seen for it, so a
// pending→running→succeeded progression across chunks lands on the final state.

import { LogEntry, TaskLog } from '../../types/DeploymentRecord';
import { stripAnsi } from './ansi';
import { redactJsonDumps } from './logFormat';

/**
 * Reassemble raw log segments into per-task, per-step reassembled text.
 * Order-preserving and split-log-safe: call with the full accumulated list of
 * entries for one response (all chunks concatenated in arrival order).
 */
export const reassembleLogEntries = (entries: LogEntry[]): TaskLog[] => {
  const order: string[] = [];
  const byTask = new Map<string, TaskLog>();

  for (const entry of entries) {
    if (!entry) continue;
    const taskName = entry.task ?? '';
    let task = byTask.get(taskName);
    if (!task) {
      task = { task: taskName, taskStatus: entry.taskStatus ?? '', steps: [] };
      byTask.set(taskName, task);
      order.push(taskName);
    }
    // Latest segment wins so the task lands on its most recent reported state.
    // These are task-level fields the backend copies onto every entry of the
    // task; carrying them through is what lets the view render the truncation
    // notice and the download link.
    if (entry.taskStatus) task.taskStatus = entry.taskStatus;
    if (entry.taskSource) task.taskSource = entry.taskSource;
    if (entry.truncated !== undefined) task.truncated = entry.truncated;
    if (entry.downloadUrl) task.downloadUrl = entry.downloadUrl;

    const stepName = entry.step ?? '';
    let step = task.steps.find(s => s.step === stepName);
    if (!step) {
      step = { step: stepName, startedAt: entry.startedAt ?? '', log: '' };
      task.steps.push(step);
    }
    step.log += entry.log ?? '';
  }

  return order.map(name => byTask.get(name) as TaskLog);
};

/**
 * Flatten reassembled logs into one plain-text blob for copy-to-clipboard. A
 * JSON-dump line (lib/logFormat `isJsonDump`) is replaced by the same
 * placeholder the view shows, so a copy can't leak what the screen withholds.
 */
export const logsToPlainText = (tasks: TaskLog[]): string =>
  tasks
    .map(task => {
      const body = task.steps
        .map(step => {
          const log = redactJsonDumps(stripAnsi(step.log));
          return step.step ? `# ${step.step}\n${log}` : log;
        })
        .join('\n');
      return `=== ${task.task} (${task.taskStatus}) ===\n${body}`;
    })
    .join('\n\n');
